import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useApp } from '../../context/AppContext';
import messageService from '../../services/messageService';
import DescriptionCompareModal from './DescriptionCompareModal';
import {
  parseDescriptionChangeMessage,
  splitTextAtDescriptionWord,
} from '../../utils/descriptionChangeMessage';
import '../../styles/components/TicketMessages.css';

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'];

const isVideoFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('video/')) return true;
  if (file.type && file.type.startsWith('audio/')) return false;
  const name = String(file.name || '').toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const isAudioFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('audio/')) return true;
  if (file.type && file.type.startsWith('video/')) return false;
  const name = String(file.name || '').toLowerCase();
  return AUDIO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const isVideoAttachment = (attachment) => {
  if (!attachment) return false;
  if (attachment.contentType?.startsWith('video/')) return true;
  const url = String(attachment.url || '').toLowerCase();
  const name = String(attachment.name || '').toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => url.includes(ext) || name.endsWith(ext));
};

const isImageAttachment = (attachment) => {
  if (!attachment?.url) return false;
  if (attachment.contentType?.startsWith('image/')) return true;
  const url = attachment.url.toLowerCase();
  return (
    url.endsWith('.jpg') ||
    url.endsWith('.jpeg') ||
    url.endsWith('.png') ||
    url.endsWith('.gif') ||
    url.endsWith('.webp')
  );
};

const MessageContent = ({ content }) => {
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const { displayText, meta } = parseDescriptionChangeMessage(content);

  if (!meta) {
    return <>{displayText}</>;
  }

  const { before, link, after } = splitTextAtDescriptionWord(displayText);

  return (
    <>
      {before}
      {link ? (
        <button
          type="button"
          className="description-change-link"
          onClick={() => setCompareModalOpen(true)}
        >
          {link}
        </button>
      ) : null}
      {after}
      <DescriptionCompareModal
        open={compareModalOpen}
        oldDescription={meta.oldDescription}
        newDescription={meta.newDescription}
        onClose={() => setCompareModalOpen(false)}
      />
    </>
  );
};

MessageContent.propTypes = {
  content: PropTypes.string,
};

const TicketMessages = ({ ticketId, refreshKey, onMessageSent }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const { addToast } = useApp();

  const fetchMessages = useCallback(async () => {
    if (!ticketId) return;

    try {
      setLoading(true);
      const response = await messageService.getByTicketId(ticketId);
      setMessages(response);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Falha ao carregar as mensagens');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    fetchMessages();
  }, [ticketId, fetchMessages]);

  useEffect(() => {
    if (refreshKey && refreshKey > 0 && ticketId) {
      fetchMessages();
    }
  }, [refreshKey, ticketId, fetchMessages]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';

    const videoFiles = files.filter(isVideoFile);
    const audioFiles = files.filter(isAudioFile);
    const otherFiles = files.filter(
      (file) => !isVideoFile(file) && !isAudioFile(file)
    );

    if (videoFiles.length > 0) {
      addToast?.(
        'Vídeos devem ser anexados na seção Descrição (Anexar vídeo), não nos comentários.',
        'error'
      );
    }

    if (audioFiles.length > 0) {
      addToast?.(
        'Áudios devem ser anexados na seção Descrição (Anexar áudio), não nos comentários.',
        'error'
      );
    }

    setAttachments(otherFiles);
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text && attachments.length === 0) return;

    try {
      setSending(true);
      setError(null);

      const attachmentsBase64 = await Promise.all(
        attachments.map((file) => convertToBase64(file))
      );

      const messageData = {
        content: text,
        ticketId,
        isSupportResponse: false,
        attachments: attachmentsBase64,
      };

      const response = await messageService.create(messageData);
      const messageWithDate = {
        ...response,
        createdDate: response.createdDate || new Date().toISOString(),
      };
      setMessages((prev) => [messageWithDate, ...prev]);
      setNewMessage('');
      setAttachments([]);
      onMessageSent?.();
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (err?.response?.status
          ? `Falha ao enviar (${err.response.status})`
          : null) ||
        err?.message ||
        'Falha ao enviar a mensagem';
      setError(apiMessage);
      addToast?.(apiMessage, 'error');
    } finally {
      setSending(false);
    }
  };

  const formatMessageDate = (date) => {
    try {
      if (!date || date === 'null' || date === 'undefined') {
        return 'Data inválida';
      }

      const messageDate = parseISO(date);

      if (isToday(messageDate)) {
        return `Hoje às ${format(messageDate, 'HH:mm')}`;
      }
      if (isYesterday(messageDate)) {
        return `Ontem às ${format(messageDate, 'HH:mm')}`;
      }
      return format(messageDate, "d 'de' MMMM 'às' HH:mm", { locale: ptBR });
    } catch (formatError) {
      console.error('Erro ao formatar data da mensagem:', formatError);
      return 'Data inválida';
    }
  };

  const canSubmit = Boolean(newMessage.trim()) || attachments.length > 0;

  const attachmentLabel = () => {
    if (attachments.length === 0) return 'Anexar arquivos';
    return `${attachments.length} arquivo(s) selecionado(s)`;
  };

  if (loading && messages.length === 0) {
    return (
      <div className="messages-loading">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Carregando mensagens...</p>
      </div>
    );
  }

  if (error && messages.length === 0 && !canSubmit) {
    return (
      <div className="messages-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button
          type="button"
          className="retry-button"
          onClick={() => {
            fetchMessages();
          }}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="ticket-messages">
      <h3>Mensagens</h3>

      <form className="message-form" onSubmit={handleSubmit}>
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Digite sua mensagem aqui..."
          rows={3}
          disabled={sending}
        />
        <div className="form-actions">
          <div className="file-input-wrapper">
            <input
              type="file"
              id="attachments"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileChange}
              className="file-input"
              disabled={sending}
            />
            <label htmlFor="attachments" className="file-label">
              <i className="fas fa-paperclip"></i>
              {attachmentLabel()}
            </label>
          </div>
          <button
            type="submit"
            disabled={sending || !canSubmit}
            className="send-button"
          >
            <i className="fas fa-paper-plane"></i>
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>

      <div className="messages-list">
        {messages.length === 0 ? (
          <div className="empty-messages">
            <i className="fas fa-comments"></i>
            <p>Nenhuma mensagem ainda</p>
            <span>Inicie a conversa enviando uma mensagem</span>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.supportResponse ? 'support-message' : 'user-message'} ${!message.read ? 'unread' : ''}`}
            >
              <div className="message-header">
                <div className="message-author-info">
                  <i
                    className={`fas ${message.supportResponse ? 'fa-headset' : 'fa-user'}`}
                  ></i>
                  <span className="message-author">
                    {message.createdBy?.name || 'Usuário Desconhecido'}
                  </span>
                  {message.supportResponse && (
                    <span className="support-badge">Equipe de Suporte</span>
                  )}
                </div>
                <span className="message-time">
                  {formatMessageDate(message.createdDate)}
                </span>
              </div>
              <div className="message-content">
                <MessageContent content={message.content} />
              </div>
              {message.attachments && message.attachments.length > 0 && (
                <div className="message-attachments">
                  {message.attachments.map((attachment, index) => {
                    if (isVideoAttachment(attachment)) {
                      return (
                        <div key={index} className="message-video-attachment">
                          <video
                            className="ticket-video-player"
                            controls
                            preload="metadata"
                            src={attachment.url}
                          />
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment-link"
                          >
                            <i className="fas fa-video"></i>
                            {attachment.name || 'Vídeo'}
                          </a>
                        </div>
                      );
                    }

                    if (isImageAttachment(attachment)) {
                      return (
                        <div key={index} className="message-image-attachment">
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={attachment.url}
                              alt={attachment.name || 'Anexo'}
                              className="attached-image"
                            />
                          </a>
                        </div>
                      );
                    }

                    return (
                      <a
                        key={index}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-link"
                      >
                        <i className="fas fa-file"></i>
                        {attachment.name}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

TicketMessages.propTypes = {
  ticketId: PropTypes.string,
  refreshKey: PropTypes.number,
  onMessageSent: PropTypes.func,
};

export default TicketMessages;
